// Post Card Component
// Individual post card for feed view

import React from 'react';
import { Post } from '../../types/community';
import { useChatRoomTheme } from '../../contexts/ChatRoomThemeContext';
import { RichBody } from './RichBody';
import {
  PostCardContainer,
  PostVoteSection,
  VoteButton,
  VoteCount,
  PostContent,
  PostTitle,
  PostBody,
  PostMeta,
  PostAuthor,
  PostTime,
  PostCommentCount,
  PostActions,
  SaveButton,
} from '../../styles/CommunityChatRooms/PostCard.styles';

interface PostCardProps {
  post: Post;
  onClick: () => void;
  onVote: (postId: string, voteType: 'upvote' | 'downvote') => void;
  onSave: (postId: string) => void;
  showFullBody?: boolean; // Show full body instead of truncated preview
  /**
   * Open the author's profile. Omitted, the name renders as plain text — the
   * card should not look interactive where nothing is listening.
   */
  onAuthorClick?: (authorId: string, authorName: string) => void;
}

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return `${Math.floor(diffInSeconds / 604800)}w ago`;
};

export const PostCard: React.FC<PostCardProps> = ({
  post,
  onClick,
  onVote,
  onSave,
  showFullBody = false,
  onAuthorClick,
}) => {
  const { theme } = useChatRoomTheme();
  const score = post.upvotes - post.downvotes;
  const isUpvoted = post.userVote === 'upvote';
  const isDownvoted = post.userVote === 'downvote';

  const handleUpvote = (e: React.MouseEvent) => {
    e.stopPropagation();
    onVote(post.id, 'upvote');
  };

  const handleDownvote = (e: React.MouseEvent) => {
    e.stopPropagation();
    onVote(post.id, 'downvote');
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSave(post.id);
  };

  return (
    <PostCardContainer $theme={theme} onClick={onClick}>
      <PostVoteSection>
        <VoteButton
          $isActive={isUpvoted}
          $direction="up"
          $theme={theme}
          onClick={handleUpvote}
          aria-label="Upvote"
        >
          ▲
        </VoteButton>
        <VoteCount $isPositive={score > 0} $theme={theme}>
          {score > 0 ? `+${score}` : score.toString()}
        </VoteCount>
        <VoteButton
          $isActive={isDownvoted}
          $direction="down"
          $theme={theme}
          onClick={handleDownvote}
          aria-label="Downvote"
        >
          ▼
        </VoteButton>
      </PostVoteSection>

      <PostContent>
        <PostTitle $theme={theme}>{post.title}</PostTitle>
        {post.body && (
          <PostBody $showFull={showFullBody} $theme={theme}>
            <RichBody text={post.body} />
          </PostBody>
        )}
        <PostMeta $theme={theme}>
          {/* Opens the profile over the room rather than navigating to it:
              leaving the chat to look someone up loses your place in the
              conversation, which is the whole reason for the drawer. */}
          <PostAuthor
            $theme={theme}
            $clickable={!!onAuthorClick && !!post.authorId}
            onClick={onAuthorClick && post.authorId
              ? (e) => {
                // The whole card opens the post; the name must not do both.
                e.stopPropagation();
                onAuthorClick(post.authorId, post.authorName);
              }
              : undefined}
            role={onAuthorClick && post.authorId ? 'button' : undefined}
            tabIndex={onAuthorClick && post.authorId ? 0 : undefined}
            onKeyDown={onAuthorClick && post.authorId
              ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onAuthorClick(post.authorId, post.authorName);
                }
              }
              : undefined}
            aria-label={onAuthorClick && post.authorId ? `View ${post.authorName}'s profile` : undefined}
          >
            {post.authorName}
          </PostAuthor>
          <PostTime>{formatTimeAgo(post.createdAt)}</PostTime>
          <PostCommentCount $theme={theme} onClick={(e) => { e.stopPropagation(); onClick(); }}>
            💬 {post.commentCount} {post.commentCount === 1 ? 'comment' : 'comments'}
          </PostCommentCount>
        </PostMeta>
      </PostContent>

      <PostActions>
        <SaveButton
          $isSaved={post.isSaved || false}
          $theme={theme}
          onClick={handleSave}
          aria-label={post.isSaved ? 'Unsave post' : 'Save post'}
        >
          {post.isSaved ? '🔖' : '📌'}
        </SaveButton>
      </PostActions>
    </PostCardContainer>
  );
};

